import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { LOADING_TYPES, useCheckoutState } from '../../../utils/checkoutState';
import { pushErrorTrackingEventFromErrorObject } from '../../../utils/tracking';
import Checkbox from '../../atoms/Checkbox';
import LoadingIndicator from '../../atoms/LoadingIndicator';
import Price from '../../atoms/Price';
import { showDefaultError } from '../../molecules/Notifications';
import './styles.scss';

const InstallmentsForm = () => {
  const { checkoutState, updatePaymentOption, isLoading, setLoading } = useCheckoutState();
  const paymentOption = checkoutState.orderState.payment?.selected?.[0];

  const [selectedNumberOfInstallments, setNumberOfInstallments] = useState(
    paymentOption?.instalment?.numberOfInstalments ?? 3
  );
  const [isDeferralSelected, setDeferral] = useState(!!paymentOption?.configuration?.deferral?.isSelected);

  if (!paymentOption?.instalment || !paymentOption.configuration) {
    return null;
  }

  const onNumberOfInstallmentsChange = async (numberOfInstallments: number) => {
    setNumberOfInstallments(numberOfInstallments);
    setLoading(LOADING_TYPES.installmentNumberChange(numberOfInstallments));
    try {
      await updatePaymentOption({
        paymentOptionKey: 'witt_pay_instalments',
        specification: {
          deferral: { isSelected: isDeferralSelected },
          instalments: [{ number: numberOfInstallments, isSelected: true }]
        }
      });
    } catch (e) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'update_payment_method');
    }
    setLoading(LOADING_TYPES.off);
  };

  const onDeferralChange = async (_isDeferralSelected: boolean) => {
    setLoading(LOADING_TYPES.installmentDeferralChange);
    setDeferral(_isDeferralSelected);
    try {
      await updatePaymentOption({
        paymentOptionKey: 'witt_pay_instalments',
        specification: {
          deferral: { isSelected: _isDeferralSelected },
          instalments: [{ number: selectedNumberOfInstallments, isSelected: true }]
        }
      });
    } catch (e) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'update_payment_method');
    }
    setLoading(LOADING_TYPES.off);
  };

  const yearlyInterestRate = checkoutState.orderState.basket.serviceCosts?.find(sc => sc.key === 'yearly_interest_rate')
    ?.value;

  const instalmentPaymentServiceCost = checkoutState.orderState.basket.serviceCosts?.find(
    sc => sc.key === 'instalment_payment_service_cost'
  )?.value;

  return (
    <div className="installments">
      <p className="installments__description">
        <FormattedMessage id="payment.paymentMethod.installmentsForm.description" />
      </p>
      <ul className="installments__options">
        {paymentOption?.configuration?.instalments?.map(({ number }) => (
          <li
            key={number}
            onClick={() => !isLoading && onNumberOfInstallmentsChange(number)}
            className={classNames('installments__option', {
              'cursor-disabled': !!isLoading && number !== selectedNumberOfInstallments,
              'installments__option--selected': number === selectedNumberOfInstallments,
              'installments__option--loading': isLoading === LOADING_TYPES.installmentNumberChange(number)
            })}
          >
            <LoadingIndicator isSmall />
            <div className="installments__data">
              <span className="installments__number">{number}</span>
              <FormattedMessage id="payment.paymentMethod.installmentsForm.installments" />
            </div>
          </li>
        ))}
      </ul>
      <div className="installments__details">
        <div className="installments__detail">
          <span>
            1. <FormattedMessage id="payment.paymentMethod.installmentsForm.installment" />
          </span>
          <Price price={paymentOption.instalment.firstInstalmentValue} />
        </div>
        <div className="installments__detail">
          <span>
            2. - {selectedNumberOfInstallments}.{' '}
            <FormattedMessage id="payment.paymentMethod.installmentsForm.monthlyInstallment" />
          </span>
          <span className="installments__detail-value">
            <span>
              <FormattedMessage id="payment.paymentMethod.installmentsForm.each" />
            </span>
            <Price price={paymentOption.instalment.subsequentInstalmentValue} />
          </span>
        </div>
      </div>
      <div className="installments__rate">
        <div className="installments__rate-info">
          <span>
            <FormattedMessage id="basket.paymentServiceFees.instalmentPaymentServiceCost" />
          </span>
          <div>
            <span className="installments__rate-number">
              {yearlyInterestRate !== undefined && (
                <FormattedNumber value={yearlyInterestRate / 10000} style="percent" maximumFractionDigits={2} />
              )}
            </span>
            <FormattedMessage id="payment.paymentMethod.installmentsForm.interestRate" />
          </div>
        </div>
        {instalmentPaymentServiceCost !== undefined && <Price price={instalmentPaymentServiceCost} />}
      </div>
      <div className="installments__deferral">
        <div className="installments__checkbox-wrapper">
          <Checkbox
            className={classNames('installments__deferral-checkbox', {
              'installments__deferral-checkbox--disabled': !!isLoading
            })}
            name="deferral"
            label={
              <FormattedMessage
                id="payment.paymentMethod.installmentsForm.deferralLabel"
                values={{ weeks: paymentOption?.configuration?.deferral?.weeks }}
              />
            }
            value={isDeferralSelected}
            dataTestId="deferral"
            onChange={_isDeferralSelected => !isLoading && onDeferralChange(_isDeferralSelected)}
          />
          {isLoading === LOADING_TYPES.installmentDeferralChange && <LoadingIndicator isSmall />}
        </div>
        <p className="installments__deferral-info">
          <FormattedMessage
            id="payment.paymentMethod.installmentsForm.deferralInfo"
            values={{ weeks: paymentOption?.configuration?.deferral?.weeks }}
          />
        </p>
      </div>
      <div className="installments__totals">
        <div className="installments__shipping-costs">
          <span>
            <FormattedMessage id="basket.shipping" />
          </span>
          <Price price={checkoutState.orderState.basket.total.costShippingServiceCosts} />
        </div>
        <div className="installments__total">
          <span>
            <FormattedMessage id="basket.total" />
          </span>
          <Price
            price={
              checkoutState?.orderState.basket.total.costCapture ??
              checkoutState.orderState.basket.total.costWithTaxWithVouchersWithServiceCostsWithGiftCard
            }
          />
        </div>
        <span className="installments__vat">
          <FormattedMessage id="basket.includingTax" />
        </span>
      </div>
    </div>
  );
};

export default InstallmentsForm;
